import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import Loader from '../Loader';
import Pagination from 'react-js-pagination';

const ENRMSRegistrationAdmin = () => {
    const [name,   setName]   = useState('');
    const [token,  setToken]  = useState('');
    const [expire, setExpire] = useState('');
    const [role,   setRole]   = useState('');
    const [access, setAccess] = useState([]);
    const [user,   setUser]   = useState(null);
    const [activeTab, setActiveTab] = useState('websites');
    const [popupOpen, setPopupOpen] = useState(true);
    const [msg, setMsg] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate = useNavigate();

    // Websites state
    const [websites,     setWebsites]     = useState([]);
    const [websiteTotal, setWebsiteTotal] = useState(0);
    const [websitePage,  setWebsitePage]  = useState(1);
    const [websiteForm,  setWebsiteForm]  = useState(null);
    const [websiteErrors, setWebsiteErrors] = useState({});

    // Risk Data Sources state
    const [sources,     setSources]     = useState([]);
    const [sourceForm,  setSourceForm]  = useState(null);
    const [sourceErrors, setSourceErrors] = useState({});

    // Risk Types state
    const [riskTypes,     setRiskTypes]     = useState([]);
    const [riskTotal,     setRiskTotal]     = useState(0);
    const [riskPage,      setRiskPage]      = useState(1);
    const [riskForm,      setRiskForm]      = useState(null);
    const [riskErrors,    setRiskErrors]    = useState({});
    const [allWebsites,   setAllWebsites]   = useState([]);

    // Email Addresses state
    const [emails,       setEmails]       = useState([]);
    const [emailTotal,   setEmailTotal]   = useState(0);
    const [emailPage,    setEmailPage]    = useState(1);
    const [emailForm,    setEmailForm]    = useState(null);
    const [emailErrors,  setEmailErrors]  = useState({});

    useEffect(() => { refreshToken(); getUserAccess(); }, []);
    useEffect(() => { if (role > 2) navigate('/dashboard'); }, [role]);
    useEffect(() => {
        if (user !== null && role !== '' && parseInt(role) > 1 && !user.hasAdmin) navigate('/dashboard');
    }, [user, role]);
    useEffect(() => { if (token && user !== null) { loadWebsites(); loadSources(); loadAllWebsites(); loadRiskTypes(); loadEmails(); } }, [token, user]);
    useEffect(() => { if (token && user !== null) loadWebsites(); }, [websitePage]);
    useEffect(() => { if (token && user !== null) loadRiskTypes(); }, [riskPage]);
    useEffect(() => { if (token && user !== null) loadEmails(); }, [emailPage]);

    const refreshToken = async () => {
        try {
            const res = await axios.get(`${baseUrl}/token`);
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setRole(decoded.role); setExpire(decoded.exp);
        } catch { navigate('/'); window.location.reload(); }
    };
    const getUserAccess = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/user-access`, { headers: { Authorization: `Bearer ${token}` } });
            setAccess(res.data.results);
            setUser({ hasAdmin: !!res.data.enrms_registration_admin_switch });
        } catch (e) { navigate('/dashboard'); }
    };

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        if (expire * 1000 < Date.now()) {
            const res = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${res.data.accessToken}`;
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setExpire(decoded.exp);
        }
        return config;
    }, err => Promise.reject(err));

    const showMsg = (text, success = true) => { setMsg(text); setIsSuccess(success); };

    // ── Websites ──────────────────────────────────────────────────────────
    const loadWebsites = async () => {
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/admin/websites?page=${websitePage}&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
            setWebsites(res.data.data); setWebsiteTotal(res.data.totalItem);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };
    const loadAllWebsites = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/admin/websites/all`, { headers: { Authorization: `Bearer ${token}` } });
            setAllWebsites(res.data.data);
        } catch (e) { console.log(e); }
    };
    const handleWebsiteSave = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!websiteForm.registration_website_description?.trim()) errs.desc = 'Description required.';
        if (!websiteForm.registration_website?.trim()) errs.url = 'Website required.';
        if (!websiteForm.registration_count_limit || isNaN(websiteForm.registration_count_limit)) errs.limit = 'Count limit required (integer).';
        setWebsiteErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.post(`${baseUrl}/registration/admin/websites/save`, websiteForm, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); setWebsiteForm(null);
            setPopupOpen(false);
            await Promise.all([loadWebsites(), loadAllWebsites()]);
        } catch (e) { showMsg(e.response?.data?.msg || 'Error saving website.', false); setPopupOpen(false); }
    };
    const handleWebsiteDelete = async (id) => {
        if (!window.confirm('Delete this website?')) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.delete(`${baseUrl}/registration/admin/websites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg);
            setPopupOpen(false);
            await Promise.all([loadWebsites(), loadAllWebsites()]);
        } catch (e) { showMsg(e.response?.data?.msg || 'Error deleting.', false); setPopupOpen(false); }
    };

    // ── Risk Data Sources ─────────────────────────────────────────────────
    const loadSources = async () => {
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/admin/risk-data-sources`, { headers: { Authorization: `Bearer ${token}` } });
            setSources(res.data.data);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };
    const handleSourceSave = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!sourceForm.risk_data_source?.trim()) errs.name = 'Source name required.';
        setSourceErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.post(`${baseUrl}/registration/admin/risk-data-sources/save`, sourceForm, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); setSourceForm(null);
            setPopupOpen(false);
            await loadSources();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error saving source.', false); setPopupOpen(false); }
    };

    // ── Risk Types ────────────────────────────────────────────────────────
    const loadRiskTypes = async () => {
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/admin/risk-types?page=${riskPage}&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
            setRiskTypes(res.data.data); setRiskTotal(res.data.totalItem);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };
    const handleRiskSave = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!riskForm.risk_data_source_id) errs.source = 'Data source required.';
        if (!riskForm.registration_risk_value?.trim()) errs.value = 'Risk value required.';
        setRiskErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.post(`${baseUrl}/registration/admin/risk-types/save`, riskForm, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); setRiskForm(null);
            setPopupOpen(false);
            await loadRiskTypes();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error saving risk type.', false); setPopupOpen(false); }
    };
    const handleRiskDelete = async (id) => {
        if (!window.confirm('Delete this risk type?')) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.delete(`${baseUrl}/registration/admin/risk-types/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg);
            setPopupOpen(false);
            await loadRiskTypes();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error deleting.', false); setPopupOpen(false); }
    };

    // ── Email Addresses ───────────────────────────────────────────────────
    const loadEmails = async () => {
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/admin/email-addresses?page=${emailPage}&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
            setEmails(res.data.data); setEmailTotal(res.data.totalItem);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };
    const handleEmailSave = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!emailForm.registration_email_address?.trim()) errs.email = 'Email address required.';
        setEmailErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.post(`${baseUrl}/registration/admin/email-addresses/save`, emailForm, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); setEmailForm(null);
            setPopupOpen(false);
            await loadEmails();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error saving email.', false); setPopupOpen(false); }
    };
    const handleEmailDelete = async (id) => {
        if (!window.confirm('Delete this email address?')) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.delete(`${baseUrl}/registration/admin/email-addresses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg);
            setPopupOpen(false);
            await loadEmails();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error deleting.', false); setPopupOpen(false); }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab); setMsg('');
        if (tab === 'websites')     { loadWebsites(); loadAllWebsites(); }
        else if (tab === 'risk-sources') loadSources();
        else if (tab === 'risk-types')   loadRiskTypes();
        else if (tab === 'emails')       loadEmails();
    };

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {(user === null || popupOpen) && <Loader />}
                <div className="content-page admin-attendance-page">
                    <div className="row">
                        <div className="col-6"><div className="left-title"><h1>Admin Registration Setup</h1></div></div>
                        <div className="col-6 inventory-btn">
                            <div className="addnew-button"><Link to="/enrms-registration"><span>&larr;</span> Back</Link></div>
                        </div>
                    </div>
                    <p className={`has-text-centered ${isSuccess ? 'success' : 'error'}`}>{msg}</p>

                    {/* Tabs */}
                    <div style={{ display:'flex', borderBottom:'2px solid #dee2e6', marginBottom:'20px' }}>
                        {['websites', 'risk-sources', 'risk-types', 'emails'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => handleTabChange(tab)}
                                style={{
                                    padding:'8px 16px', border:'none', background:'none', cursor:'pointer',
                                    borderBottom: activeTab === tab ? '2px solid #0d6efd' : '2px solid transparent',
                                    marginBottom:'-2px',
                                    color: activeTab === tab ? '#0d6efd' : '#555',
                                    fontWeight: activeTab === tab ? '600' : 'normal'
                                }}
                            >
                                {{ websites: 'Registration Websites', 'risk-sources': 'Risk Data Sources', 'risk-types': 'Risk Types', emails: 'Email Addresses' }[tab]}
                            </button>
                        ))}
                    </div>

                    {/* ── Websites Tab ── */}
                    {activeTab === 'websites' && (
                        <div className="row">
                            <div className="col-12 common-white-shadow-background setting-section">
                                <div className="row">
                                    <div className="col-12 inventory-btn">
                                        {!websiteForm && (
                                            <div className="addnew-button">
                                                <a href="#" onClick={e => { e.preventDefault(); setWebsiteForm({ registration_website_description: '', registration_website: '', registration_count_limit: 9999 }); setWebsiteErrors({}); }}>Add</a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {websiteForm && (
                                    <div className="row" style={{ marginBottom: '15px', padding: '15px', background: '#f9f9f9', borderRadius: '6px' }}>
                                        <h4 style={{ marginBottom: '10px' }}>{websiteForm.registration_website_id ? 'Edit Website' : 'Add Website'}</h4>
                                        <div className="col-md-3">
                                            <label>Description *</label>
                                            <input type="text" className="form-control" value={websiteForm.registration_website_description} onChange={e => setWebsiteForm({ ...websiteForm, registration_website_description: e.target.value })} />
                                            {websiteErrors.desc && <div className="error error-message">{websiteErrors.desc}</div>}
                                        </div>
                                        <div className="col-md-3">
                                            <label>Website URL *</label>
                                            <input type="text" className="form-control" value={websiteForm.registration_website} onChange={e => setWebsiteForm({ ...websiteForm, registration_website: e.target.value })} />
                                            {websiteErrors.url && <div className="error error-message">{websiteErrors.url}</div>}
                                        </div>
                                        <div className="col-md-2">
                                            <label>Count Limit *</label>
                                            <input type="number" className="form-control" value={websiteForm.registration_count_limit} onChange={e => setWebsiteForm({ ...websiteForm, registration_count_limit: e.target.value })} />
                                            {websiteErrors.limit && <div className="error error-message">{websiteErrors.limit}</div>}
                                        </div>
                                        <div className="col-md-4" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                            <button className="btn btn-primary" onClick={handleWebsiteSave}>Save</button>
                                            <button className="btn btn-secondary" onClick={() => setWebsiteForm(null)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                <div className="fullwidth-table track-table-body">
                                    <table>
                                        <thead><tr><th style={{ width:'auto' }}>ID</th><th style={{ width:'auto' }}>Description</th><th style={{ width:'auto' }}>Website</th><th style={{ width:'auto' }}>Count Limit</th><th style={{ width:'auto' }}>Actions</th></tr></thead>
                                        <tbody>
                                            {websites.map((row, i) => (
                                                <tr key={i}>
                                                    <td style={{ textAlign:'left' }}>{row.registration_website_id}</td>
                                                    <td style={{ textAlign:'left' }}>{row.registration_website_description}</td>
                                                    <td style={{ textAlign:'left' }}>{row.registration_website}</td>
                                                    <td style={{ textAlign:'left' }}>{row.registration_count_limit}</td>
                                                    <td style={{ textAlign:'left' }}>
                                                        <a href="#" onClick={e => { e.preventDefault(); setWebsiteForm({ ...row, id: row.registration_website_id }); setWebsiteErrors({}); }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/bx-edit.png`} alt="Edit" />
                                                        </a>
                                                        <a href="#" onClick={e => { e.preventDefault(); handleWebsiteDelete(row.registration_website_id); }} style={{ marginLeft: '8px' }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/delete.png`} alt="Delete" />
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {websiteTotal > 20 && <Pagination activePage={websitePage} itemsCountPerPage={20} totalItemsCount={websiteTotal} pageRangeDisplayed={5} onChange={setWebsitePage} />}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Risk Data Sources Tab ── */}
                    {activeTab === 'risk-sources' && (
                        <div className="row">
                            <div className="col-12 common-white-shadow-background setting-section">
                                <div className="row">
                                    <div className="col-12 inventory-btn">
                                        {!sourceForm && (
                                            <div className="addnew-button">
                                                <a href="#" onClick={e => { e.preventDefault(); setSourceForm({ risk_data_source: '' }); setSourceErrors({}); }}>Add</a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {sourceForm && (
                                    <div className="row" style={{ marginBottom: '15px', padding: '15px', background: '#f9f9f9', borderRadius: '6px' }}>
                                        <div className="col-md-4">
                                            <label>Source Name *</label>
                                            <input type="text" className="form-control" value={sourceForm.risk_data_source} onChange={e => setSourceForm({ ...sourceForm, risk_data_source: e.target.value })} />
                                            {sourceErrors.name && <div className="error error-message">{sourceErrors.name}</div>}
                                        </div>
                                        <div className="col-md-4" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                            <button className="btn btn-primary" onClick={handleSourceSave}>Save</button>
                                            <button className="btn btn-secondary" onClick={() => setSourceForm(null)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                <div className="fullwidth-table track-table-body">
                                    <table>
                                        <thead><tr><th style={{ width:'auto' }}>ID</th><th style={{ width:'auto' }}>Source Name</th><th style={{ width:'auto' }}>Edit</th></tr></thead>
                                        <tbody>
                                            {sources.map((row, i) => (
                                                <tr key={i}>
                                                    <td style={{ textAlign:'left' }}>{row.risk_data_source_id}</td>
                                                    <td style={{ textAlign:'left' }}>{row.risk_data_source}</td>
                                                    <td style={{ textAlign:'left' }}>
                                                        <a href="#" onClick={e => { e.preventDefault(); setSourceForm({ ...row, id: row.risk_data_source_id }); setSourceErrors({}); }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/bx-edit.png`} alt="Edit" />
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Risk Types Tab ── */}
                    {activeTab === 'risk-types' && (
                        <div className="row">
                            <div className="col-12 common-white-shadow-background setting-section">
                                <div className="row">
                                    <div className="col-12 inventory-btn">
                                        {!riskForm && (
                                            <div className="addnew-button">
                                                <a href="#" onClick={e => { e.preventDefault(); setRiskForm({ risk_data_source_id: '', registration_risk_value: '', registration_site_id: '' }); setRiskErrors({}); }}>Add</a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {riskForm && (
                                    <div className="row" style={{ marginBottom: '15px', padding: '15px', background: '#f9f9f9', borderRadius: '6px' }}>
                                        <div className="col-md-3">
                                            <label>Data Source *</label>
                                            <select className="form-control" value={riskForm.risk_data_source_id} onChange={e => setRiskForm({ ...riskForm, risk_data_source_id: e.target.value })}>
                                                <option value="">— Select —</option>
                                                {sources.map(s => <option key={s.risk_data_source_id} value={s.risk_data_source_id}>{s.risk_data_source}</option>)}
                                            </select>
                                            {riskErrors.source && <div className="error error-message">{riskErrors.source}</div>}
                                        </div>
                                        <div className="col-md-3">
                                            <label>Risk Value *</label>
                                            <input type="text" className="form-control" value={riskForm.registration_risk_value} onChange={e => setRiskForm({ ...riskForm, registration_risk_value: e.target.value })} placeholder="e.g. Spam, Normal, Unknown, 0, 1" />
                                            {riskErrors.value && <div className="error error-message">{riskErrors.value}</div>}
                                        </div>
                                        <div className="col-md-3">
                                            <label>Registration Website (None = no registration needed)</label>
                                            <select className="form-control" value={riskForm.registration_site_id} onChange={e => setRiskForm({ ...riskForm, registration_site_id: e.target.value })}>
                                                <option value="">None</option>
                                                {allWebsites.map(w => <option key={w.registration_website_id} value={w.registration_website_id}>{w.registration_website_description}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                            <button className="btn btn-primary" onClick={handleRiskSave}>Save</button>
                                            <button className="btn btn-secondary" onClick={() => setRiskForm(null)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                <div className="fullwidth-table track-table-body">
                                    <table>
                                        <thead><tr><th style={{ width:'auto' }}>ID</th><th style={{ width:'auto' }}>Data Source</th><th style={{ width:'auto' }}>Risk Value</th><th style={{ width:'auto' }}>Website</th><th style={{ width:'auto' }}>Actions</th></tr></thead>
                                        <tbody>
                                            {riskTypes.map((row, i) => (
                                                <tr key={i}>
                                                    <td style={{ textAlign:'left' }}>{row.registration_risk_type_id}</td>
                                                    <td style={{ textAlign:'left' }}>{row.risk_data_source}</td>
                                                    <td style={{ textAlign:'left' }}>{row.registration_risk_value}</td>
                                                    <td style={{ textAlign:'left' }}>{row.registration_website_description || 'None'}</td>
                                                    <td style={{ textAlign:'left' }}>
                                                        <a href="#" onClick={e => { e.preventDefault(); setRiskForm({ ...row, id: row.registration_risk_type_id }); setRiskErrors({}); }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/bx-edit.png`} alt="Edit" />
                                                        </a>
                                                        <a href="#" onClick={e => { e.preventDefault(); handleRiskDelete(row.registration_risk_type_id); }} style={{ marginLeft: '8px' }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/delete.png`} alt="Delete" />
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {riskTotal > 20 && <Pagination activePage={riskPage} itemsCountPerPage={20} totalItemsCount={riskTotal} pageRangeDisplayed={5} onChange={setRiskPage} />}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Email Addresses Tab ── */}
                    {activeTab === 'emails' && (
                        <div className="row">
                            <div className="col-12 common-white-shadow-background setting-section">
                                <div className="row">
                                    <div className="col-12 inventory-btn">
                                        {!emailForm && (
                                            <div className="addnew-button">
                                                <a href="#" onClick={e => { e.preventDefault(); setEmailForm({ registration_email_address: '' }); setEmailErrors({}); }}>Add</a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {emailForm && (
                                    <div className="row" style={{ marginBottom: '15px', padding: '15px', background: '#f9f9f9', borderRadius: '6px' }}>
                                        <div className="col-md-4">
                                            <label>Email Address *</label>
                                            <input type="email" className="form-control" value={emailForm.registration_email_address} onChange={e => setEmailForm({ ...emailForm, registration_email_address: e.target.value })} />
                                            {emailErrors.email && <div className="error error-message">{emailErrors.email}</div>}
                                        </div>
                                        <div className="col-md-4" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                                            <button className="btn btn-primary" onClick={handleEmailSave}>Save</button>
                                            <button className="btn btn-secondary" onClick={() => setEmailForm(null)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                <div className="fullwidth-table track-table-body">
                                    <table>
                                        <thead><tr><th style={{ width:'auto' }}>ID</th><th style={{ width:'auto' }}>Email Address</th><th style={{ width:'auto' }}>Actions</th></tr></thead>
                                        <tbody>
                                            {emails.map((row, i) => (
                                                <tr key={i}>
                                                    <td style={{ textAlign:'left' }}>{row.registration_email_address_id}</td>
                                                    <td style={{ textAlign:'left' }}>{row.registration_email_address}</td>
                                                    <td style={{ textAlign:'left' }}>
                                                        <a href="#" onClick={e => { e.preventDefault(); setEmailForm({ ...row, id: row.registration_email_address_id }); setEmailErrors({}); }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/bx-edit.png`} alt="Edit" />
                                                        </a>
                                                        <a href="#" onClick={e => { e.preventDefault(); handleEmailDelete(row.registration_email_address_id); }} style={{ marginLeft: '8px' }}>
                                                            <img src={`${process.env.PUBLIC_URL}/images/delete.png`} alt="Delete" />
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {emailTotal > 20 && <Pagination activePage={emailPage} itemsCountPerPage={20} totalItemsCount={emailTotal} pageRangeDisplayed={5} onChange={setEmailPage} />}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ENRMSRegistrationAdmin;
